import { animated, AnimatedComponent, FrameValue } from '@react-spring/web';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import styled from '@tanium/react-emotion-9';
import { useResizeObserver } from '@tanium/react-use-resize-observer';

import { useAggregatedRef } from '../../hooks/useAggregatedRef';
import { AxisOrientation } from './AxisOrientation';

const isSafari = () =>
  window.navigator.userAgent.toLowerCase().indexOf('chrome') === -1 &&
  window.navigator.userAgent.toLowerCase().indexOf('safari') > -1;

export interface TickLabelProps {
  /**
   * The text to display in the label.
   */
  children: string;
  /**
   * The color of the tick label.
   */
  color?: string;
  /**
   * The font size of the tick label.
   */
  fontSize?: number;
  /**
   * The height available for the tick label. This is only applicable when `orientation` is `top` or
   * `bottom`.
   */
  height?: number;
  /**
   * A left (x) offset for the text label's anchor point.
   */
  left: FrameValue<number>;
  /**
   * The maximum number of lines to allow the text to wrap to. If the text does not fit on this many
   * lines, it will be truncated.
   */
  // FUTURE: allow this prop to be any (positive) number FUTURE: allow this prop to be optional and
  // infer a value based on available space
  maxLineCount: 1 | 2;
  /**
   * Handler to invoke when the pointer moves over the tick label.
   */
  onPointerOver?: React.MouseEventHandler;
  /**
   * Handler to invoke when the pointer moves out from tick label.
   */
  onPointerOut?: React.MouseEventHandler;
  /**
   * The orientation of the tick label relative to the point the tick is labeling.
   */
  orientation: AxisOrientation;
  /**
   * A top (y) offset for the text label's anchor point.
   */
  top: FrameValue<number>;
  /**
   * The width available for the tick label. This is only applicable when `orientation` is `left` or
   * `right`.
   */
  width?: number;
  /**
   * The opacity of the tick label.
   */
  opacity: FrameValue<number>;
}

/**
 * Apply no workarounds. This should always be true in Chrome and in any supported browser when the
 * text can always be broken within one line.
 */
const WORKAROUND_NONE = 0;

/**
 * Force unwrappable text to be wrapped at any opportunity using `word-break: break-all` instead of
 * `break-word`. This applies to Firefox in cases where unbreakable text overflows the first line of
 * the container, otherwise causing the container to appear empty.
 */
const WORKAROUND_CSS_BREAK = 1;

/**
 * Insert `<wbr />` before every character in the text. This applies to Safari in cases where
 * unbreakable text overflows the first line of the container, in which cases the pure CSS
 * workaround above fails.
 */
const WORKAROUND_WBRS = 2;

/**
 * The number of the last workaround available, to prevent overshooting.
 */
const WORKAROUND_LAST = WORKAROUND_WBRS;

const LINE_HEIGHT_PADDING_PX = 2;

const getAdjustments = ({
  top,
  left,
  width,
  height,
  lineHeight,
  orientation,
}: {
  top: FrameValue<number>;
  left: FrameValue<number>;
  width: number;
  height: number;
  lineHeight: number;
  orientation: AxisOrientation;
}) => {
  switch (orientation) {
    case 'top':
      return {
        // adjust based on 3/4 the first line's height
        x: left.to((l) => l - Math.sqrt(2) * lineHeight * (3 / 4)),
        y: top,
      };
    case 'left':
      return {
        // account for width, since this text is right-aligned
        x: left.to((l) => l - width),
        y: top,
      };
    case 'right':
      return {
        x: left,
        // transform-origin ensures text will be vertically centered
        y: top,
      };
    case 'bottom':
    default: {
      // this adjustment counters the shape-outline height/width, which visually should "appear"
      // outside the container
      const shapeAdjustment = (Math.sqrt(2) * height) / 2;
      return {
        // target alignment 1/4 of the way down the first line's height
        x: left.to((l) => l - width + shapeAdjustment - (Math.sqrt(2) * lineHeight) / 4),
        y: top.to((t) => t - shapeAdjustment),
      };
    }
  }
};

export const OuterContainer = styled(animated.div)({
  position: 'absolute',
  pointerEvents: 'none',
}) as unknown as AnimatedComponent<'div'>;

export const ContentContainer = styled('div')<{
  color: string | undefined;
  fontSize: number;
  isRotated: boolean;
  lineHeight: number;
  maxHeight: number;
  maxLineCount: number;
  textAlign: 'left' | 'right';
}>(({ color, fontSize, isRotated, lineHeight, maxHeight, maxLineCount, textAlign }) => ({
  color,
  fontSize,
  lineHeight: `${lineHeight}px`,
  maxHeight,
  textAlign,
  transform: isRotated ? 'rotate(-45deg)' : 'translate(0, -50%)',
  transformOrigin: textAlign === 'left' ? '0 0' : '100% 0',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitLineClamp: maxLineCount,
  WebkitBoxOrient: 'vertical',
  display: '-webkit-box',
}));

export const TickRotationPlaceholder = styled('div', {
  shouldForwardProp: (p) => !['height', 'isRight', 'width'].includes(p),
})<{ height: number; isRight?: boolean; width: number }>(({ height, isRight, width }) => ({
  shapeOutside: `polygon(0 0, 100% 100%, ${isRight ? '100% 0' : '0 100%'})`,
  float: isRight ? 'right' : 'left',
  width,
  height,
}));

export const ContentSpan = styled('span')({
  pointerEvents: 'auto',
});

export const TickLabel = ({
  children,
  color,
  fontSize = 12,
  left,
  height = 100,
  maxLineCount,
  onPointerOver,
  onPointerOut,
  opacity,
  orientation,
  top,
  width = 100,
}: TickLabelProps): JSX.Element => {
  const isRotated = orientation === 'bottom' || orientation === 'top';
  const textAlign = orientation === 'bottom' || orientation === 'left' ? 'right' : 'left';

  const lineHeight = fontSize + LINE_HEIGHT_PADDING_PX;
  const maxTextHeight = lineHeight * maxLineCount;

  const computedTextWidth = isRotated ? Math.sqrt(2) * height + maxTextHeight : width;

  const [workaround, setWorkaround] = useState(WORKAROUND_NONE);
  const lastRenderedWorkaround = useRef(workaround);

  const wrapperRef = useRef<HTMLDivElement>();

  const detectBadRendering = useCallback(
    (node: HTMLDivElement) => {
      // Browser workaround: unbreakable strings are rendered after the floats, and thus outside the
      // visible area. This can be detected by comparing the wrapper div's client height to the
      // expected max height. If we detect this problem, we can use a workaround to allow the text
      // to break instead of being effectively invisible.
      const badRenderingDetected = node.clientHeight > maxTextHeight;

      if (badRenderingDetected) {
        setWorkaround(Math.min(WORKAROUND_LAST, lastRenderedWorkaround.current + 1));
      }
    },
    [maxTextHeight],
  );

  useLayoutEffect(() => {
    // reset and re-detect whether a workaround is (still?) needed
    setWorkaround(WORKAROUND_NONE);

    if (wrapperRef.current) {
      detectBadRendering(wrapperRef.current);
    }
  }, [children, detectBadRendering]);

  // Keep this effect last so that other effects know which workaround(s) were applied
  useEffect(() => {
    lastRenderedWorkaround.current = workaround;
  }, [workaround]);

  const resizeRef = useResizeObserver<HTMLDivElement>(
    useCallback(
      (e) => {
        detectBadRendering(e.target as HTMLDivElement);
      },
      [detectBadRendering],
    ),
  );

  const combinedRefs = useAggregatedRef(useMemo(() => [wrapperRef, resizeRef], [wrapperRef, resizeRef]));

  const { x, y } = getAdjustments({
    top,
    left,
    width: computedTextWidth,
    height: maxTextHeight,
    lineHeight,
    orientation,
  });

  const content = useMemo(() => {
    // FUTURE: it could make sense to add some custom break logic here to handle browsers that don't
    // soft-break at plausible opportunities (e.g., slashes). It might also make sense to make this
    // customizable via props somehow so it can be tailored for specific data.

    if (workaround === WORKAROUND_WBRS) {
      return children.split('').reduce<React.ReactNode[]>((acc, cur, i) => {
        // eslint-disable-next-line react/no-array-index-key -- no better key available
        return acc.concat(<wbr key={`wbr-${i}`} />, cur);
      }, []);
    }

    return children;
  }, [children, workaround]);

  // The style prop is used below to avoid creating too many temporary classes for variables that
  // might change rapidly or have many possible values
  return (
    <OuterContainer
      style={{
        left: x,
        top: y,
        opacity,
      }}
    >
      <ContentContainer
        color={color}
        fontSize={fontSize}
        isRotated={isRotated}
        lineHeight={lineHeight}
        maxHeight={maxTextHeight}
        maxLineCount={maxLineCount}
        textAlign={textAlign}
        style={{
          width: computedTextWidth,
          wordBreak: workaround === WORKAROUND_CSS_BREAK ? 'break-all' : 'break-word',
        }}
      >
        <div
          ref={combinedRefs}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
          style={{
            // Safari fix - explicitly setting overflow here to avoid a safari quirk where the overflow value of a parent higher up the tree
            // seems to take precedence.
            overflow: isSafari() ? 'hidden' : undefined,
          }}
        >
          {isRotated && (
            <>
              <TickRotationPlaceholder height={maxTextHeight} width={maxTextHeight} />
              <TickRotationPlaceholder height={maxTextHeight} width={maxTextHeight} isRight />
            </>
          )}
          <ContentSpan>{content}</ContentSpan>
        </div>
      </ContentContainer>
    </OuterContainer>
  );
};
